/**
 * Dithering kernels.
 *
 * All three are error-diffusion or ordered threshold operations over an RGB
 * float buffer; they run before quantisation so that a 256- or 16-colour target
 * keeps gradients readable instead of banding.
 */

export type DitherMode = 'none' | 'floyd-steinberg' | 'atkinson' | 'ordered';

export interface RgbBuffer {
  width: number;
  height: number;
  /** RGB triplets, 0..255, mutable. */
  data: Float32Array;
}

export function makeBuffer(width: number, height: number): RgbBuffer {
  return { width, height, data: new Float32Array(width * height * 3) };
}

/** 8x8 Bayer matrix, normalised to -0.5..0.5. */
const BAYER_8 = (() => {
  const base = [
    [0, 32, 8, 40, 2, 34, 10, 42],
    [48, 16, 56, 24, 50, 18, 58, 26],
    [12, 44, 4, 36, 14, 46, 6, 38],
    [60, 28, 52, 20, 62, 30, 54, 22],
    [3, 35, 11, 43, 1, 33, 9, 41],
    [51, 19, 59, 27, 49, 17, 57, 25],
    [15, 47, 7, 39, 13, 45, 5, 37],
    [63, 31, 55, 23, 61, 29, 53, 21],
  ];
  return base.map((row) => row.map((v) => v / 64 - 0.5));
})();

/** Error-diffusion weights: [dx, dy, weight]. */
const FLOYD: Array<[number, number, number]> = [
  [1, 0, 7 / 16],
  [-1, 1, 3 / 16],
  [0, 1, 5 / 16],
  [1, 1, 1 / 16],
];

const ATKINSON: Array<[number, number, number]> = [
  [1, 0, 1 / 8],
  [2, 0, 1 / 8],
  [-1, 1, 1 / 8],
  [0, 1, 1 / 8],
  [1, 1, 1 / 8],
  [0, 2, 1 / 8],
];

/**
 * Quantises a buffer in place with the given dither mode.
 *
 * `quantise` maps an RGB triple to the nearest representable colour; the
 * difference is diffused to neighbouring pixels (or pre-biased, for ordered
 * dithering) so the eye averages it back out.
 */
export function ditherBuffer(
  buffer: RgbBuffer,
  mode: DitherMode,
  quantise: (r: number, g: number, b: number) => [number, number, number],
  /** Strength for ordered dithering, in colour steps. */
  orderedStrength = 32,
): void {
  const { width, height, data } = buffer;

  if (mode === 'none') {
    for (let i = 0; i < data.length; i += 3) {
      const [r, g, b] = quantise(data[i], data[i + 1], data[i + 2]);
      data[i] = r;
      data[i + 1] = g;
      data[i + 2] = b;
    }
    return;
  }

  if (mode === 'ordered') {
    for (let y = 0; y < height; y++) {
      for (let x = 0; x < width; x++) {
        const i = (y * width + x) * 3;
        const bias = BAYER_8[y % 8][x % 8] * orderedStrength;
        const [r, g, b] = quantise(
          clamp(data[i] + bias),
          clamp(data[i + 1] + bias),
          clamp(data[i + 2] + bias),
        );
        data[i] = r;
        data[i + 1] = g;
        data[i + 2] = b;
      }
    }
    return;
  }

  const kernel = mode === 'atkinson' ? ATKINSON : FLOYD;
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      const i = (y * width + x) * 3;
      const or = data[i];
      const og = data[i + 1];
      const ob = data[i + 2];
      const [qr, qg, qb] = quantise(or, og, ob);
      data[i] = qr;
      data[i + 1] = qg;
      data[i + 2] = qb;
      const er = or - qr;
      const eg = og - qg;
      const eb = ob - qb;
      for (const [dx, dy, weight] of kernel) {
        const nx = x + dx;
        const ny = y + dy;
        if (nx < 0 || ny < 0 || nx >= width || ny >= height) continue;
        const ni = (ny * width + nx) * 3;
        data[ni] = clamp(data[ni] + er * weight);
        data[ni + 1] = clamp(data[ni + 1] + eg * weight);
        data[ni + 2] = clamp(data[ni + 2] + eb * weight);
      }
    }
  }
}

function clamp(v: number): number {
  return v < 0 ? 0 : v > 255 ? 255 : v;
}

/** Monochrome threshold with optional dithering, for Braille and ASCII modes. */
export function ditherMono(
  luminance: Float32Array,
  width: number,
  height: number,
  threshold: number,
  mode: DitherMode,
): Uint8Array {
  const out = new Uint8Array(width * height);
  const buf = Float32Array.from(luminance);

  if (mode === 'none' || mode === 'ordered') {
    for (let y = 0; y < height; y++) {
      for (let x = 0; x < width; x++) {
        const i = y * width + x;
        const bias = mode === 'ordered' ? BAYER_8[y % 8][x % 8] * 255 : 0;
        out[i] = buf[i] + bias >= threshold ? 1 : 0;
      }
    }
    return out;
  }

  const kernel = mode === 'atkinson' ? ATKINSON : FLOYD;
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      const i = y * width + x;
      const old = buf[i];
      const on = old >= threshold ? 1 : 0;
      out[i] = on;
      const error = old - (on ? 255 : 0);
      for (const [dx, dy, weight] of kernel) {
        const nx = x + dx;
        const ny = y + dy;
        if (nx < 0 || ny < 0 || nx >= width || ny >= height) continue;
        buf[ny * width + nx] += error * weight;
      }
    }
  }
  return out;
}
